import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { type Skill } from '@/lib/api/skills.js';

import {
  buildSlashSections,
  SLASH_TOKEN,
  type SlashMenuSection,
  type SlashSkillOption,
} from './slash-skill-match.js';

export {
  dedupeByTriggerPrivateFirst,
  pinnedFirst,
  scopePrecedence,
  type SlashSkillOption,
} from './slash-skill-match.js';

/** A skill picked for the next send — via the slash popup or the directory's "Use" action. */
export interface SelectedSkill {
  id: string;
  title: string;
  trigger: string;
}

/** The trailing "/token" being typed at the end of the composer's text, or null. */
export interface SlashState {
  open: boolean;
  query: string;
}

export interface UseSlashSkillPickerArgs<T extends SlashSkillOption = Skill> {
  /** The draft text. The caller owns it; this hook only reads it. */
  value: string;
  /** Caret offset into {@link value} — the token is matched immediately before it. */
  caretPos: number;
  /** The skills kill switch. While false no token is ever detected, so no popup can open. */
  skillsEnabled: boolean;
  /** The cached skill catalog the popup matches against — the caller owns the query. */
  skills: T[] | undefined;
  /** Skills already carried as chips, for the duplicate and cap rules below. */
  selectedSkills: SelectedSkill[];
  /** How many chips one message may carry. */
  maxSkills: number;
  /** Adds the picked skill as a chip. */
  addSkill: (skill: SelectedSkill) => void;
  /**
   * The draft edit a pick implies: the typed `/token` stripped out and the caret at the seam.
   * The caller owns the text and the textarea, so it applies this itself.
   */
  onTokenReplaced: (nextValue: string, nextCaretPos: number) => void;
  /**
   * Called instead of the default no-op when a pick hits the cap (not a duplicate — those still
   * strip their now-redundant token as usual). Omit to keep the composer's own behavior: the draft
   * is left untouched, so a chat message the user is mid-typing around the cap never loses text.
   * A caller that opts in gets its token stripped too, exactly as a successful pick would — this
   * flag exists for a caller (the automation dialogs) whose field is a form input, not a message
   * still being composed, so a stuck `/trigger` left in place is a bug, not a safety net.
   */
  onCapExceeded?: () => void;
}

export interface UseSlashSkillPickerResult<T extends SlashSkillOption = Skill> {
  /** Non-null while the trailing text matches the slash-trigger regex — drives `SlashCommandMenu`. */
  slashState: SlashState | null;
  /** The popup's sections for `slashState`'s query — empty when nothing matches. */
  slashSections: SlashMenuSection<T>[];
  /** Every skill in {@link slashSections}, flattened in the order the popup renders them. */
  slashMatches: T[];
  /** Index into {@link slashMatches} currently highlighted — Enter picks this one. */
  slashHighlightIndex: number;
  /** Sets the highlighted index — the caller wires the arrow keys and the popup's mouse hover to it. */
  setSlashHighlightIndex: Dispatch<SetStateAction<number>>;
  /** Converts the currently-typed slash token into a chip (popup click/Enter path). */
  selectSlashSkill: (skill: T) => void;
  /** Dismisses the popup for the current query — it re-opens once the query changes. */
  dismissSlash: () => void;
}

/**
 * The slash-command skill picker: everything between "the user typed `/pp`" and "a chip exists and
 * the token is gone". It owns the token detection, the popup's open/query state, the Escape latch
 * and the highlighted match — and nothing about the draft itself. The caller keeps the text and the
 * caret, passes them in, and applies the edit this hook hands back through `onTokenReplaced`, which
 * is what lets a second surface (the automation dialogs) reuse the picker over its own input.
 */
export function useScopedSlashSkillPicker<T extends SlashSkillOption = Skill>({
  value,
  caretPos,
  skillsEnabled,
  skills,
  selectedSkills,
  maxSkills,
  addSkill,
  onTokenReplaced,
  onCapExceeded,
}: UseSlashSkillPickerArgs<T>): UseSlashSkillPickerResult<T> {
  /**
   * The trailing `/query` immediately BEFORE THE CARET (not the string's end), matching `/
   * <trigger-so-far>` at position 0 or right after whitespace — or `null` when there is none.
   * Anchoring on the caret (rather than the whole value) means a `/token` that the user has since
   * moved away from (caret repositioned earlier in the draft, or more text typed after it)
   * correctly stops matching — and a `/token` typed mid-draft with existing trailing text still
   * matches.
   */
  const slashQuery = useMemo<string | null>(() => {
    if (!skillsEnabled) return null;
    const textBeforeCaret = value.slice(0, caretPos);
    const match = SLASH_TOKEN.exec(textBeforeCaret);
    return match ? (match[1] ?? '') : null;
  }, [value, caretPos, skillsEnabled]);

  /**
   * The query Escape last dismissed the popup for — `slashState.open` stays false as long as
   * `slashQuery` hasn't changed since. Cleared on ANY `slashQuery` change (not only a transition to
   * `null`) — the effect's dependency array is `[slashQuery]` alone, so React re-runs it exactly
   * when the value actually changes, which is precisely "a new query arrived" (whether that's a
   * different trigger, a shorter one from backspacing, or the same text retyped from scratch after
   * clearing the token entirely). Clearing only on a transition to `null` left a stale dismissal in
   * place: `/ppt` -> Escape (dismissed "ppt") -> backspace to `/pp` (query changes, but the old
   * effect only fired on transitions to null, so "ppt" stayed the dismissed value) -> retype `t`
   * back to `/ppt` -> query equals the still-stale dismissed value -> popup silently stays closed
   * with no second Escape. Unconditionally clearing on every query change fixes this: the
   * backspace-to-"pp" step above already clears the dismissal, so the popup reopens both there and
   * on the retype.
   *
   * The dependency is deliberate and reads as UNNECESSARY to `react-hooks/exhaustive-deps`, which
   * sees an effect body using only the setter and advises dropping the array's one entry. Taking
   * that advice would make the effect fire on every render. It is there so React compares it.
   */
  const [dismissedSlashQuery, setDismissedSlashQuery] = useState<string | null>(
    null,
  );
  useEffect(() => {
    setDismissedSlashQuery(null);
  }, [slashQuery]);

  /** What the popup lists for the current query: pinned, recent and all skills on a bare `/`, one
   *  ranked section otherwise. Empty while the token is absent or nothing matches. */
  const slashSections = useMemo<SlashMenuSection<T>[]>(() => {
    if (slashQuery === null) return [];
    return buildSlashSections(skills ?? [], slashQuery);
  }, [slashQuery, skills]);

  /** The sections flattened in render order — the list Enter, Tab and the arrow keys walk. */
  const slashMatches = useMemo<T[]>(
    () => slashSections.flatMap((section) => section.skills),
    [slashSections],
  );

  /** Drives `SlashCommandMenu`. `open` is false while Escape has dismissed the popup for the
   *  current query — see `dismissedSlashQuery` above — and while nothing matches, so the popup
   *  never shows an empty state and Enter falls through to a normal send. */
  const slashState = useMemo<SlashState | null>(() => {
    if (slashQuery === null) return null;
    return {
      open: slashQuery !== dismissedSlashQuery && slashMatches.length > 0,
      query: slashQuery,
    };
  }, [slashQuery, dismissedSlashQuery, slashMatches]);

  /** Index into `slashMatches` highlighted by ArrowUp/ArrowDown — Enter picks this one. Reset to 0
   *  whenever the match list itself changes (new query, or the skills query resolves/updates). */
  const [slashHighlightIndex, setSlashHighlightIndex] = useState(0);
  useEffect(() => {
    setSlashHighlightIndex(0);
  }, [slashMatches]);

  /**
   * Strips the `/token` immediately before the caret out of `value` —
   * preserving whatever text follows the token boundary — and adds `skill` as a
   * chip. Used by the popup's click and by Enter.
   */
  const selectSlashSkill = useCallback(
    (skill: T): void => {
      const alreadySelected = selectedSkills.some(
        (selected) => selected.id === skill.id,
      );
      const atCap = !alreadySelected && selectedSkills.length >= maxSkills;
      /**
       * At the chip cap, with no `onCapExceeded` given, `addSkill` would silently no-op, so
       * stripping the typed `/trigger` here would eat the user's text with nothing to show for it
       * — keep the draft untouched instead (the composer's own behavior). A caller that passes
       * `onCapExceeded` opts out of that safety net — its token is stripped below like any other
       * pick, and the callback is its chance to say why nothing was attached. A duplicate pick
       * always falls through regardless: its chip already exists, so removing the redundant token
       * IS the intent.
       */
      if (atCap && !onCapExceeded) {
        return;
      }
      const before = value.slice(0, caretPos);
      const after = value.slice(caretPos);
      const match = SLASH_TOKEN.exec(before);
      const suffix = match ? after.replace(/^[\p{L}\p{N}-]*/u, '') : after;
      /**
       * The matched token includes the whitespace character in front of it, and that character is
       * put back verbatim rather than as a space: `SLASH_TOKEN` accepts any `\s`, so flattening it
       * would turn a newline into a space and silently join two lines of a multi-line prompt — an
       * automation's Prompt field is exactly where a `/trigger` gets typed at the start of a line.
       */
      const prefix = match
        ? before.slice(0, before.length - (match[1]?.length ?? 0) - 1)
        : before;
      if (atCap) {
        onCapExceeded?.();
      } else {
        addSkill({ id: skill.id, title: skill.title, trigger: skill.trigger });
      }
      onTokenReplaced(prefix + suffix, prefix.length);
    },
    [
      addSkill,
      caretPos,
      maxSkills,
      onCapExceeded,
      onTokenReplaced,
      selectedSkills,
      value,
    ],
  );

  /**
   * Dismisses the popup for the current query through the same latch Escape
   * uses, so an outside click can't leave the menu stuck open (still
   * intercepting ArrowUp/ArrowDown/Enter). Re-opens once the query changes.
   */
  const dismissSlash = useCallback((): void => {
    if (slashQuery !== null) {
      setDismissedSlashQuery(slashQuery);
    }
  }, [slashQuery]);

  return {
    slashState,
    slashSections,
    slashMatches,
    slashHighlightIndex,
    setSlashHighlightIndex,
    selectSlashSkill,
    dismissSlash,
  };
}

/** Chat revalidates its shared skill cache; scoped consumers retain their own catalog authority. */
export function useSlashSkillPicker<T extends SlashSkillOption = Skill>(
  args: UseSlashSkillPickerArgs<T>,
): UseSlashSkillPickerResult<T> {
  const result = useScopedSlashSkillPicker(args);
  const queryClient = useQueryClient();
  const active = result.slashState !== null;
  useEffect(() => {
    if (active) void queryClient.invalidateQueries({ queryKey: ['skills'] });
  }, [active, queryClient]);
  return result;
}
